/**
===================================================
Module: frontend.src.components.TenantAuditLog.tsx
Description: Permission change history for tenant super administrators
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, apiService } from "../services/api";
import { RbacAuditLogEntry } from "../types/tenant";
import TopBar from "./TopBar";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/TenantAdmin.css";

const PAGE_SIZE = 50;

const TenantAuditLog: React.FC = () => {
  const { t } = useTranslation("tenant");

  const [logs, setLogs] = useState<RbacAuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const load = useCallback(
    async (nextOffset: number) => {
      setIsLoading(true);
      try {
        const response = await apiService.getTenantAuditLogs({
          limit: PAGE_SIZE,
          offset: nextOffset,
        });
        setLogs((prev) =>
          nextOffset === 0 ? response.logs : [...prev, ...response.logs],
        );
        setTotal(response.total);
        setOffset(nextOffset);
      } catch (error) {
        setNotifications((prev) => [
          ...prev,
          {
            id: Date.now().toString(),
            type: "error",
            message:
              error instanceof ApiError && error.needsOtp
                ? t("errors.otpRequired")
                : error instanceof Error
                  ? error.message
                  : t("errors.generic"),
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    load(0);
  }, [load]);

  /** Render the JSON detail blob as readable "field: from → to" lines. */
  const describe = (entry: RbacAuditLogEntry): string => {
    const details = entry.details;
    if (!details || Object.keys(details).length === 0) return "-";

    return Object.entries(details)
      .map(([key, value]) => {
        if (
          value &&
          typeof value === "object" &&
          !Array.isArray(value) &&
          "to" in (value as Record<string, unknown>)
        ) {
          const change = value as { from?: unknown; to?: unknown };
          return `${key}: ${String(change.from ?? "-")} → ${String(change.to ?? "-")}`;
        }
        return `${key}: ${Array.isArray(value) ? value.join(", ") : String(value)}`;
      })
      .join("; ");
  };

  return (
    <div className="tenant-container">
      <div className="tenant-content">
        <TopBar />

        <div className="tenant-header-section">
          <div className="breadcrumb">
            <span
              className="breadcrumb-link"
              onClick={() => (window.location.href = "/projects")}
            >
              {t("common:home", { defaultValue: "Home" })}
            </span>
            <span className="breadcrumb-separator">/</span>
            <span>{t("audit.title")}</span>
          </div>

          <div className="tenant-title-row">
            <div>
              <h1>{t("audit.title")}</h1>
              <p className="tenant-subtitle">{t("audit.subtitle")}</p>
            </div>
          </div>
        </div>

        <table className="tenant-table">
          <thead>
            <tr>
              <th>{t("audit.columns.time")}</th>
              <th>{t("audit.columns.action")}</th>
              <th>{t("audit.columns.actor")}</th>
              <th>{t("audit.columns.target")}</th>
              <th>{t("audit.columns.details")}</th>
              <th>{t("audit.columns.ip")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && logs.length === 0 && (
              <tr>
                <td colSpan={6}>{t("audit.loading")}</td>
              </tr>
            )}
            {!isLoading && logs.length === 0 && (
              <tr>
                <td colSpan={6}>{t("audit.empty")}</td>
              </tr>
            )}
            {logs.map((entry) => (
              <tr key={entry.id}>
                <td>
                  {entry.event_time
                    ? new Date(entry.event_time).toLocaleString()
                    : "-"}
                </td>
                <td>
                  {t(`audit.actions.${entry.action}`, {
                    defaultValue: entry.action,
                  })}
                </td>
                <td>{entry.actor_email || "-"}</td>
                <td>{entry.target_email || entry.group_name || "-"}</td>
                <td className="detail-cell">{describe(entry)}</td>
                <td>{entry.ip_address || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {logs.length < total && (
          <div className="load-more-row">
            <button
              className="btn-secondary"
              onClick={() => load(offset + PAGE_SIZE)}
              disabled={isLoading}
            >
              {t("audit.loadMore")}
            </button>
          </div>
        )}
      </div>

      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default TenantAuditLog;
